/**
 * Local-first persistence (localStorage) of the chat state. Zero-knowledge:
 * EVERYTHING lives only on the client; the plaintext conversation and the private keys
 * are never uploaded. The relay only holds opaque envelopes.
 */

import type { PersistedState } from "./types";
import { decode, encode } from "./serialize";

const DEVICE_KEY = "susurro:device-id";
const statePrefix = "susurro:state:";

function stateKey(deviceId: string): string {
  return `${statePrefix}${deviceId}`;
}

function randomDeviceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  let b64 = "";
  for (const b of bytes) {
    b64 += String.fromCodePoint(b);
  }
  return `dev-${btoa(b64).replaceAll(/[+/=]/g, "").slice(0, 12)}`;
}

/** Stable device id (guest-first: the identity belongs to the device). */
export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) {
    return existing;
  }
  const id = randomDeviceId();
  localStorage.setItem(DEVICE_KEY, id);
  return id;
}

export function loadState(deviceId: string): PersistedState | undefined {
  const raw = localStorage.getItem(stateKey(deviceId));
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = decode(raw);
    if (typeof parsed !== "object" || parsed === null || !("identity" in parsed)) {
      return undefined;
    }
    // Trust boundary: it is our own persisted blob (same scheme),
    // not an external input. The engine tolerates missing collections.
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return parsed as PersistedState;
  } catch {
    return undefined;
  }
}

export function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(stateKey(state.identity.deviceId), encode(state));
  } catch (err) {
    console.warn("[chat] failed to persist state", err);
  }
}

/**
 * Adopts a state RESTORED from an E2E backup: persists it and sets its identity
 * as the active one of this browser (the next boot starts with it).
 */
export function adoptRestoredState(state: PersistedState): void {
  saveState(state);
  localStorage.setItem(DEVICE_KEY, state.identity.deviceId);
}
